import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Editor from '@monaco-editor/react';
import API_ENDPOINTS from '../ApiEndPoints';
import BackupManager from '../backups/BackupManager';
import WebpageLogs from './WebpageLogs';

const HelpTooltip = ({ text }) => {
    const [visible, setVisible] = useState(false);

    if (!text) return null;
    return (
        <div
            className="relative inline-block ml-1"
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
        >
            <div className="text-gray-400 cursor-help hover:text-gray-600">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            </div>
            {visible && (
                <div className="absolute z-50 w-64 p-2 mt-2 text-xs text-white transform -translate-x-1/2 bg-gray-800 rounded shadow-lg left-1/2 top-full">
                    {text}
                </div>
            )}
        </div>
    );
};

const WebpageSettings = ({ pageId }) => {
    const [activeTab, setActiveTab] = useState('settings');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [autoUpdatingDesc, setAutoUpdatingDesc] = useState(false);
    const [autoUpdatingImage, setAutoUpdatingImage] = useState(false);
    const [data, setData] = useState({
        page: {
            obj_attributes: {},
            properties: {}
        },
        templates: [],
        pages: [],
        lists: [],
        forms: [],
        reports: [],
        accessGroups: [],
        formFields: [],
        current_user_is_admin: false,
    });
    const [message, setMessage] = useState(null);
    const [showFormFields, setShowFormFields] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [exportData, setExportData] = useState('');
    const [importMode, setImportMode] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);

    // Related Objects State
    const [relatedObjects, setRelatedObjects] = useState({ attached_child_objs: [], attached_items: [] });
    const [attachType, setAttachType] = useState(null); // 'list', 'report', 'record'
    const [selectedAttachTid, setSelectedAttachTid] = useState('');
    const [selectedListForRecord, setSelectedListForRecord] = useState('');
    const [listItems, setListItems] = useState([]);
    const [selectedRecordId, setSelectedRecordId] = useState('');
    const [recordName, setRecordName] = useState('');

    // Helpers to resolve nested field values and keep datetime-local inputs synced with DB format.
    const getFieldValue = (path) => path.split('.').reduce((obj, key) => (obj ? obj[key] : undefined), data);

    const formatDateTimeForInput = (value) => {
        if (!value) {
            return '';
        }
        let normalized = `${value}`.trim();
        if (!normalized || normalized.startsWith('0000-00-00')) {
            return '';
        }
        normalized = normalized.replace(/\//g, '-');

        if (!normalized.includes('T')) {
            if (normalized.includes(' ')) {
                normalized = normalized.replace(' ', 'T');
            } else {
                normalized = `${normalized}T00:00`;
            }
        }

        const [datePartRaw, timePartRaw = ''] = normalized.split('T');
        const datePart = datePartRaw;
        let timePart = timePartRaw.replace(/Z$/, '');

        if (!timePart) {
            timePart = '00:00';
        } else if (timePart.length === 2) {
            timePart = `${timePart}:00`;
        } else if (timePart.length === 5) {
            // already HH:MM
        } else if (timePart.length >= 8) {
            timePart = timePart.slice(0, 5);
        }

        return `${datePart}T${timePart}`;
    };

    const normalizeDateTimeForSave = (value) => {
        if (!value) {
            return null;
        }
        const normalized = value.includes('T') ? value : value.replace(' ', 'T');
        const [datePartRaw, rawTime = '00:00'] = normalized.split('T');
        const datePart = datePartRaw.replace(/\//g, '-');
        let timePart = rawTime;
        if (timePart.length === 5) {
            timePart = `${timePart}:00`;
        } else if (timePart.length > 8) {
            timePart = timePart.slice(0, 8);
        }
        if (!timePart) {
            timePart = '00:00:00';
        }
        return `${datePart} ${timePart}`;
    };

    useEffect(() => {
        loadData();
    }, [pageId]);

    useEffect(() => {
        if (activeTab === 'related') {
            loadRelatedObjects();
        }
    }, [activeTab]);

    useEffect(() => {
        if (selectedListForRecord) {
            loadListItems(selectedListForRecord);
        }
    }, [selectedListForRecord]);

    useEffect(() => {
        if (!data.current_user_is_admin && activeTab === 'import/export') {
            setActiveTab('settings');
        }
    }, [data.current_user_is_admin, activeTab]);

    useEffect(() => {
        if (activeTab !== 'import/export' && importMode) {
            setImportMode(false);
        }
    }, [activeTab, importMode]);

    useEffect(() => {
        setExportData('');
        setImportMode(false);
        setExporting(false);
        setImporting(false);
    }, [pageId]);

    const loadRelatedObjects = async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.webpages.getRelatedObjects(pageId));
            setRelatedObjects(response.data);
        } catch (error) {
            console.error("Error loading related objects:", error);
        }
    };

    const loadListItems = async (listTid) => {
        try {
            const response = await axios.get(API_ENDPOINTS.webpages.getListItems(pageId, listTid));
            setListItems(response.data.items);
        } catch (error) {
            console.error("Error loading list items:", error);
        }
    };

    const handleAttachObject = async () => {
        if (!selectedAttachTid) return;
        try {
            await axios.post(API_ENDPOINTS.webpages.attachObject(pageId), { tid: selectedAttachTid });
            setMessage({ type: 'success', text: 'Object attached successfully.' });
            setAttachType(null);
            setSelectedAttachTid('');
            loadRelatedObjects();
        } catch (error) {
            console.error("Error attaching object:", error);
            setMessage({ type: 'error', text: 'Failed to attach object.' });
        }
    };

    const handleAttachItem = async () => {
        if (!selectedRecordId) return;
        try {
            await axios.post(API_ENDPOINTS.webpages.attachItem(pageId), { item_id: selectedRecordId, item_name: recordName });
            setMessage({ type: 'success', text: 'Item attached successfully.' });
            setAttachType(null);
            setSelectedRecordId('');
            setRecordName('');
            loadRelatedObjects();
        } catch (error) {
            console.error("Error attaching item:", error);
            setMessage({ type: 'error', text: 'Failed to attach item.' });
        }
    };

    const handleDetachObject = async (id) => {
        if (!confirm('Are you sure you want to detach this object?')) return;
        try {
            await axios.delete(API_ENDPOINTS.webpages.detachObject(pageId, id));
            setMessage({ type: 'success', text: 'Object detached successfully.' });
            loadRelatedObjects();
        } catch (error) {
            console.error("Error detaching object:", error);
            setMessage({ type: 'error', text: 'Failed to detach object.' });
        }
    };

    const handleDetachItem = async (id) => {
        if (!confirm('Are you sure you want to detach this item?')) return;
        try {
            await axios.delete(API_ENDPOINTS.webpages.detachItem(pageId, id));
            setMessage({ type: 'success', text: 'Item detached successfully.' });
            loadRelatedObjects();
        } catch (error) {
            console.error("Error detaching item:", error);
            setMessage({ type: 'error', text: 'Failed to detach item.' });
        }
    };

    const handleAutoUpdateDescription = async () => {
        if (autoUpdatingDesc) return;
        try {
            setAutoUpdatingDesc(true);
            const response = await axios.post(API_ENDPOINTS.webpages.autoUpdateDescription(pageId));
            if (response.data.success) {
                setData(prev => ({
                    ...prev,
                    page: {
                        ...prev.page,
                        description: response.data.description,
                        obj_attributes: {
                            ...prev.page.obj_attributes,
                            short_description: response.data.short_description,
                            index_image_url: response.data.index_image_url || prev.page.obj_attributes?.index_image_url
                        }
                    }
                }));
                setIsDirty(true);
                setMessage({ type: 'success', text: 'Description updated successfully.' });
            }
        } catch (error) {
            console.error("Error auto updating description:", error);
            setMessage({ type: 'error', text: 'Failed to update description.' });
        } finally {
            setAutoUpdatingDesc(false);
        }
    };

    const handleAutoUpdateImage = async () => {
        if (autoUpdatingImage) return;
        try {
            setAutoUpdatingImage(true);
            const response = await axios.post(API_ENDPOINTS.webpages.autoUpdateImage(pageId));
            if (response.data.success) {
                setData(prev => ({
                    ...prev,
                    page: {
                        ...prev.page,
                        obj_attributes: {
                            ...prev.page.obj_attributes,
                            index_image_url: response.data.url
                        }
                    }
                }));
                setIsDirty(true);
                setMessage({ type: 'success', text: 'Image updated successfully.' });
            }
        } catch (error) {
            console.error("Error auto updating image:", error);
            setMessage({ type: 'error', text: 'Failed to update image.' });
        } finally {
            setAutoUpdatingImage(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setMessage({ type: 'success', text: 'Copied to clipboard!' });
            setTimeout(() => setMessage(null), 2000);
        }, (err) => {
            console.error('Could not copy text: ', err);
        });
    };

    const handleExportDataFetch = async () => {
        if (!data.current_user_is_admin || exporting) {
            return;
        }
        try {
            setExporting(true);
            const response = await axios.get(API_ENDPOINTS.webpages.export(pageId));
            setExportData(response.data.export_data || '');
            setImportMode(false);
            setMessage({ type: 'success', text: 'Export data generated.' });
        } catch (error) {
            console.error('Error exporting page data:', error);
            setMessage({ type: 'error', text: 'Failed to export page data.' });
        } finally {
            setExporting(false);
        }
    };

    const handleImportConfirm = async () => {
        if (!data.current_user_is_admin || importing) {
            return;
        }
        if (!hasExportData) {
            setMessage({ type: 'error', text: 'Provide JSON data before importing.' });
            return;
        }
        try {
            JSON.parse(exportData);
        } catch (error) {
            setMessage({ type: 'error', text: 'Import data must be valid JSON.' });
            return;
        }
        if (!window.confirm('Importing will overwrite this webpage. Continue?')) {
            return;
        }
        try {
            setImporting(true);
            await axios.post(API_ENDPOINTS.webpages.import(pageId), { import_data: exportData });
            setMessage({ type: 'success', text: 'Import completed successfully.' });
            setImportMode(false);
            await loadData();
        } catch (error) {
            console.error('Error importing page data:', error);
            const errorText = error.response?.data?.error || 'Failed to import data.';
            setMessage({ type: 'error', text: errorText });
        } finally {
            setImporting(false);
        }
    };

    const handleDownloadExport = () => {
        if (!hasExportData) return;
        const blob = new Blob([exportData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const filename = `${data.page?.tid || 'webpage'}-export.json`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const loadData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(API_ENDPOINTS.webpages.getSettings(pageId));
            // Ensure obj_attributes exists
            const pageData = response.data.page;
            if (!pageData.obj_attributes) pageData.obj_attributes = {};
            if (!pageData.properties) pageData.properties = {};

            setData({
                ...response.data,
                page: pageData
            });
            setIsDirty(false);
        } catch (error) {
            console.error("Error loading settings:", error);
            setMessage({ type: 'error', text: 'Failed to load settings.' });
        } finally {
            setLoading(false);
        }
    };

    const fetchBackups = async () => {
        const response = await axios.get(API_ENDPOINTS.webpages.getBackups(pageId));
        return response.data.backups || [];
    };

    const fetchBackupDetail = async (backupId) => {
        const response = await axios.get(API_ENDPOINTS.webpages.getBackupDetail(pageId, backupId));
        return response.data.backup;
    };

    const restoreBackupFromApi = async (backupId) => {
        return axios.post(API_ENDPOINTS.webpages.restoreBackup(pageId, backupId));
    };

    const handleBackupRestored = () => {
        setMessage({ type: 'success', text: 'Backup restored successfully.' });
        loadData();
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let val;
        if (type === 'checkbox') {
            val = checked ? 1 : 0;
        } else if (type === 'datetime-local') {
            val = normalizeDateTimeForSave(value);
        } else {
            val = value;
        }

        // Handle nested properties (e.g., page.title or page.obj_attributes.some_attr)
        if (name.includes('.')) {
            const parts = name.split('.');
            setData(prevData => {
                // Deep clone to avoid mutation
                const newData = JSON.parse(JSON.stringify(prevData));
                let current = newData;
                for (let i = 0; i < parts.length - 1; i++) {
                    if (!current[parts[i]]) current[parts[i]] = {};
                    current = current[parts[i]];
                }
                current[parts[parts.length - 1]] = val;
                return newData;
            });
        } else {
            setData(prevData => ({
                ...prevData,
                [name]: val
            }));
        }
        setIsDirty(true);
    };

    const handleEditorChange = (value, event, name) => {
        // Handle nested properties (e.g., page.properties.cb_html)
        if (name.includes('.')) {
            const parts = name.split('.');
            setData(prevData => {
                // Deep clone to avoid mutation
                const newData = JSON.parse(JSON.stringify(prevData));
                let current = newData;
                for (let i = 0; i < parts.length - 1; i++) {
                    if (!current[parts[i]]) current[parts[i]] = {};
                    current = current[parts[i]];
                }
                current[parts[parts.length - 1]] = value;
                return newData;
            });
        } else {
            setData(prevData => ({
                ...prevData,
                [name]: value
            }));
        }
        setIsDirty(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);
        try {
            await axios.post(API_ENDPOINTS.webpages.updateSettings(pageId), data.page);
            setMessage({ type: 'success', text: 'Settings saved successfully.' });
            setIsDirty(false);
        } catch (error) {
            console.error("Error saving settings:", error);
            setMessage({ type: 'error', text: 'Failed to save settings.' });
        } finally {
            setSaving(false);
        }
    };

    const renderSelect = (label, name, options, helpText = null, valueKey = 'id', labelKey = 'title', tooltip = null) => {
        const fieldValue = getFieldValue(name);
        return (
        <div className="col-span-1">
            <div className="flex items-center mb-1">
                <label htmlFor={name} className="block text-sm font-medium text-gray-700">{label}</label>
                {tooltip && <HelpTooltip text={tooltip} />}
            </div>
            <div className="">
                {/* Standard form field styling: mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm */}
                <select
                    id={name}
                    name={name}
                    value={fieldValue ?? ''}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                    <option value=""></option>
                    {options.map(opt => (
                        <option key={opt[valueKey]} value={opt[valueKey]}>{opt[labelKey]}</option>
                    ))}
                </select>
            </div>
            {helpText && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}
        </div>
    );
    };

    const renderInput = (label, name, type = 'text', helpText = null, tooltip = null) => {
        const rawValue = getFieldValue(name);
        const inputValue = type === 'datetime-local'
            ? formatDateTimeForInput(rawValue)
            : (rawValue ?? '');
        return (
        <div className="col-span-1">
            <div className="flex items-center mb-1">
                <label htmlFor={name} className="block text-sm font-medium text-gray-700">{label}</label>
                {tooltip && <HelpTooltip text={tooltip} />}
            </div>
            <div className="">
                {/* Standard form field styling: mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm */}
                <input
                    type={type}
                    name={name}
                    id={name}
                    value={inputValue}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
            </div>
            {helpText && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}
        </div>
    );
    };

    const renderCheckbox = (label, name, helpText = null, tooltip = null) => {
        const val = getFieldValue(name);
        return (
            <div className="flex items-start col-span-1">
                <div className="flex items-center h-5">
                    <input
                        id={name}
                        name={name}
                        type="checkbox"
                        checked={val == 1}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                </div>
                <div className="ml-3 text-sm">
                    <div className="flex items-center">
                        <label htmlFor={name} className="font-medium text-gray-700">{label}</label>
                        {tooltip && <HelpTooltip text={tooltip} />}
                    </div>
                    {helpText && <p className="text-gray-500">{helpText}</p>}
                </div>
            </div>
        );
    };

    const pageEditorUrl = data?.page?.id ? `/webpages/edit_cb/${data.page.id}` : null;

    const renderActionButtons = () => (
        <div className="flex flex-wrap justify-end gap-2 mb-6">
            <a href="/webpages" className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-yellow-500 border border-transparent rounded-md shadow-sm hover:bg-yellow-600">
                Cancel
            </a>
            {isDirty && (
                <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 disabled:opacity-50"
                >
                    {saving ? 'Saving...' : 'Save Settings'}
                </button>
            )}
            <a
                href={data.page.url || '#'}
                target="_blank"
                rel="noreferrer"
                className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${data.page.url ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'}`}
                aria-disabled={!data.page.url}
            >
                Browse
            </a>
            <a
                href={pageEditorUrl || '#'}
                target="_blank"
                rel="noreferrer"
                className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${pageEditorUrl ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-300 cursor-not-allowed'}`}
                aria-disabled={!pageEditorUrl}
            >
                Page Editor
            </a>
        </div>
    );

    const TAB_CONFIG = [
        { key: 'settings', label: 'Settings', icon: 'ti-settings' },
        { key: 'html', label: 'HTML Code', icon: 'ti-code' },
        { key: 'styles', label: 'Styles', icon: 'ti-paint-bucket' },
        { key: 'logs', label: 'Logs', icon: 'ti-clipboard' },
        { key: 'backups', label: 'Backups', icon: 'ti-archive' },
        { key: 'related', label: 'Linked Objects', icon: 'ti-link' },
        { key: 'import/export', label: 'Import/Export', icon: 'ti-exchange-vertical' },
    ];
    const tabOptions = data.current_user_is_admin
        ? TAB_CONFIG
        : TAB_CONFIG.filter(tab => tab.key !== 'import/export');
    const renderTabIcon = (iconClass) => iconClass ? <i className={`${iconClass} text-base leading-none`}></i> : null;
    const hasExportData = Boolean(exportData && exportData.trim().length);

    if (loading) return <div className="p-4 text-center">Loading settings...</div>;

    return (
        <div className="overflow-hidden bg-white rounded-lg shadow">
            <div className="px-8 py-6 space-y-4 bg-white border-b border-gray-200 panel-heading md:px-10 md:py-6">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <h3 className="text-2xl font-semibold tracking-tight text-gray-900">{data.page?.title || 'Untitled Page'} Page Settings</h3>

                </div>

                {message && (
                    <div className={`mt-2 px-4 py-2 rounded ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {message.text}
                    </div>
                )}
            </div>

            <div className="px-6 pb-6">
                <div className="mb-4">
                    <div className="p-2 overflow-x-auto border border-gray-200 rounded-md shadow-sm bg-white/70">
                        <div className="flex flex-wrap justify-between gap-2">
                            {tabOptions.map((tab) => {
                                const isActive = activeTab === tab.key;
                                return (
                                    <button
                                        key={tab.key}
                                        onClick={() => setActiveTab(tab.key)}
                                        className={`flex flex-1 basis-0 min-w-[140px] items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition text-center focus:outline-none focus:ring-2 focus:ring-indigo-200 ${
                                            isActive
                                                ? 'bg-white text-indigo-600 shadow border border-indigo-200'
                                                : 'text-gray-500 border border-transparent hover:text-gray-700 hover:border-gray-200'
                                        }`}
                                    >
                                        {renderTabIcon(tab.icon)}
                                        <span>{tab.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                {activeTab === 'settings' && (
                    <div className="space-y-6">
                        {renderActionButtons()}

                        {/* Primary Fields */}
                        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-4">
                            {renderInput("Title", "page.title", "text", "Used as SEO Title", "The title of the webpage. Used in the browser tab, search engine results (SEO), and as the default heading.")}
                            {renderInput("TID", "page.tid", "text", data.page.category_id == 0 ? `Web Url: ${data.page.url}` : (data.page.category_id == 2 ? `Short Code: {{COMP:${data.page.tid}}}` : ""), "A unique text identifier for the page. Used in URLs for pages, or as a reference code for templates and components.")}
                            {data.page.category_id == 0 && renderSelect("Page Template", "page.obj_attributes.page_template_tid", data.templates, null, "tid", "title", "The master template that wraps this page. Controls the header, footer, and overall layout structure.")}
                        </div>

                        {/* Advanced Toggle */}
                        <div className="flex items-center">
                            <button
                                type="button"
                                onClick={() => setShowAdvanced(!showAdvanced)}
                                className="flex items-center text-sm text-indigo-600 hover:text-indigo-900 focus:outline-none"
                            >
                                {showAdvanced ? (
                                    <>
                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" /></svg>
                                        Hide Advanced Settings
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                        Show Advanced Settings
                                    </>
                                )}
                            </button>
                        </div>

                        {showAdvanced && (
                            <div className="pt-6 space-y-6 border-t border-gray-200">
                                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-4">
                                    {/* Column 1 */}
                                    <div className="space-y-4">
                                        <div className="col-span-1">
                                            <div className="flex items-center mb-1">
                                                <label htmlFor="page.category_id" className="block text-sm font-medium text-gray-700">Type</label>
                                                <HelpTooltip text="Defines the object type. 'Page' is a viewable URL. 'Template' is a layout wrapper. 'Component' is a reusable part." />
                                            </div>
                                            <select
                                                id="page.category_id"
                                                name="page.category_id"
                                                value={data.page.category_id || 0}
                                                onChange={handleInputChange}
                                                className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            >
                                                <option value="0">Page</option>
                                                <option value="1">Template</option>
                                                <option value="2">Component</option>
                                            </select>
                                            <p className="mt-1 text-xs text-gray-500">Pages are primary, templates are containers, components are reusable.</p>
                                        </div>
                                        {renderSelect("Parent Page", "page.parent_object_definitions_id", data.pages, "Allows referencing child pages via template engine.", "id", "title", "Organizes pages into a hierarchy. Child pages can inherit properties or be listed in navigation menus under the parent.")}
                                        {renderInput("Tags", "page.tags", "text", null, "Comma-separated keywords for categorization. Used for searching, filtering, and grouping pages internally.")}
                                    </div>

                                    {/* Column 2 */}
                                    <div className="space-y-4">
                                        {data.page.category_id == 0 && (
                                            <div className="col-span-1">
                                                <div className="flex items-center mb-1">
                                                    <label htmlFor="page.obj_attributes.use_template_engine" className="block text-sm font-medium text-gray-700">Use Template Engine</label>
                                                    <HelpTooltip text="Enables the Smarty template engine. Allows dynamic logic, variables, and loops within the page content." />
                                                </div>
                                                <select
                                                    id="page.obj_attributes.use_template_engine"
                                                    name="page.obj_attributes.use_template_engine"
                                                    value={data.page.obj_attributes?.use_template_engine || 0}
                                                    onChange={handleInputChange}
                                                    className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                >
                                                    <option value="0">No</option>
                                                    <option value="1">Yes</option>
                                                </select>
                                                <p className="mt-1 text-xs text-gray-500">Process Smarty Template code.</p>
                                            </div>
                                        )}
                                        <div className="col-span-1">
                                            <div className="flex items-center mb-1">
                                                <label htmlFor="page.active" className="block text-sm font-medium text-gray-700">Status</label>
                                                <HelpTooltip text="Controls visibility. 'Active' pages are public. 'Archived' pages are inaccessible but preserved." />
                                            </div>
                                            <select
                                                id="page.active"
                                                name="page.active"
                                                value={data.page.active || 0}
                                                onChange={handleInputChange}
                                                className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            >
                                                <option value="0">Archived</option>
                                                <option value="1">Active</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Column 3 */}
                                    <div className="space-y-4">
                                        {renderInput("Rank", "page.rank", "text", null, "Numeric value for sorting. Lower numbers usually appear first in menus and lists.")}
                                        <div className="col-span-1">
                                            <div className="flex items-center mb-1">
                                                <label htmlFor="page.locked" className="block text-sm font-medium text-gray-700">Logged in contacts only</label>
                                                <HelpTooltip text="Restricts access to logged-in users only. If checked, anonymous visitors cannot view this page." />
                                            </div>
                                            <select
                                                id="page.locked"
                                                name="page.locked"
                                                value={data.page.locked || 0}
                                                onChange={handleInputChange}
                                                className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            >
                                                <option value="0">No</option>
                                                <option value="1">Yes</option>
                                            </select>
                                        </div>
                                        {data.page.locked == 1 && (
                                            <>
                                                {renderSelect("Access Group", "page.obj_attributes.access_group_tid", data.accessGroups, null, "tid", "title", "Specific user group allowed to access this page. Requires 'Logged in contacts only' to be enabled.")}
                                                {renderSelect("Redirect To", "page.obj_attributes.cant_access_redirect_to_tid", data.pages, null, "tid", "title", "Where to send unauthorized users. Usually a login page or an error page.")}
                                            </>
                                        )}
                                    </div>

                                    {/* Column 4 */}
                                    <div className="space-y-4">
                                        {renderSelect("Linked Form", "page.obj_attributes.form_tid", data.forms, "Pages can use fields in the linked form.", "tid", "title", "Connects a data form to this page. Allows the page to display or edit records from that form.")}
                                        {data.page.obj_attributes?.form_tid && (
                                            <div className="text-xs text-gray-500 break-all">
                                                {data.page.tid}?{data.page.obj_attributes.form_tid}={'{record_id}'}
                                            </div>
                                        )}

                                        {data.formFields && data.formFields.length > 0 && (
                                            <div className="mt-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowFormFields(!showFormFields)}
                                                    className="text-xs text-indigo-600 hover:text-indigo-900"
                                                >
                                                    {showFormFields ? 'Hide Form Fields' : 'Show Form Fields'}
                                                </button>
                                                {showFormFields && (
                                                    <div className="mt-2 overflow-y-auto border border-gray-200 rounded max-h-40">
                                                        <table className="min-w-full divide-y divide-gray-200">
                                                            <thead className="bg-gray-50">
                                                                <tr>
                                                                    <th className="px-2 py-1 text-xs font-medium text-left text-gray-500 uppercase">Type</th>
                                                                    <th className="px-2 py-1 text-xs font-medium text-left text-gray-500 uppercase">Name</th>
                                                                    <th className="px-2 py-1 text-xs font-medium text-left text-gray-500 uppercase">Code</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="bg-white divide-y divide-gray-200">
                                                                {data.formFields.map((field, idx) => (
                                                                    <tr key={idx}>
                                                                        <td className="px-2 py-1 text-xs text-gray-500">{field.type || '(missing)'}</td>
                                                                        <td className="px-2 py-1 text-xs text-gray-900">{field.label ? field.label.substring(0, 20) : '(missing)'}</td>
                                                                        <td className="px-2 py-1 font-mono text-xs text-gray-500">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => copyToClipboard(`{{FIELD:${field.name}}}`)}
                                                                                className="text-indigo-600 hover:text-indigo-900 hover:underline focus:outline-none"
                                                                                title="Click to copy"
                                                                            >
                                                                                {`{{FIELD:${field.name || '(missing)'}}}`}
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Description Section */}
                                {data.page.category_id == 0 && (
                                    <div className="pt-4 space-y-4 border-t border-gray-200">
                                        <div>
                                    <div className="flex items-center mb-1">
                                        <label htmlFor="page.description" className="block text-sm font-medium text-gray-700">Description (SEO)</label>
                                        <HelpTooltip text="Meta description for search engines. Summarizes the page content for search results." />
                                    </div>
                                    <textarea
                                        id="page.description"
                                        name="page.description"
                                        rows={3}
                                        value={data.page.description || ''}
                                        onChange={handleInputChange}
                                        className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                </div>                                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                            <div>
                                                <div className="flex items-center justify-between mb-1">
                                                    <div className="flex items-center">
                                                        <label htmlFor="page.obj_attributes.short_description" className="block text-sm font-medium text-gray-700">Short Description</label>
                                                        <HelpTooltip text="Brief summary used in page lists, cards, or teasers within the site." />
                                                    </div>
                                                    {!data.newpage && (
                                                        <button
                                                            type="button"
                                                            onClick={handleAutoUpdateDescription}
                                                            disabled={autoUpdatingDesc}
                                                            className={`text-xs px-2 py-1 rounded flex items-center ${autoUpdatingDesc ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                                                        >
                                                            {autoUpdatingDesc && (
                                                                <svg className="w-3 h-3 mr-2 -ml-1 text-gray-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                            )}
                                                            {autoUpdatingDesc ? 'Generating...' : 'Auto Update'}
                                                        </button>
                                                    )}
                                                </div>
                                        <p className="mb-1 text-xs text-gray-500">Used as introductory description in indexes.</p>
                                        <textarea
                                            id="page.obj_attributes.short_description"
                                            name="page.obj_attributes.short_description"
                                            rows={6}
                                            value={data.page.obj_attributes?.short_description || ''}
                                            onChange={handleInputChange}
                                            className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        />
                                    </div>                                            <div className="space-y-4">
                                                <h4 className="text-lg font-medium text-gray-900">Options</h4>
                                                <div className="col-span-1">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <div className="flex items-center">
                                                            <label htmlFor="page.obj_attributes.index_image_url" className="block text-sm font-medium text-gray-700">Place Holder Image URL</label>
                                                            <HelpTooltip text="Thumbnail image for the page. Displayed in page lists, social media shares, and cards." />
                                                        </div>
                                                        {!data.newpage && (
                                                            <button
                                                                type="button"
                                                                onClick={handleAutoUpdateImage}
                                                                disabled={autoUpdatingImage}
                                                                className={`text-xs px-2 py-1 rounded flex items-center ${autoUpdatingImage ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                                                            >
                                                                {autoUpdatingImage && (
                                                                    <svg className="w-3 h-3 mr-2 -ml-1 text-gray-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                    </svg>
                                                                )}
                                                                {autoUpdatingImage ? 'Updating...' : 'Auto Update'}
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="mt-1">
                                                        <input
                                                            type="text"
                                                            name="page.obj_attributes.index_image_url"
                                                            id="page.obj_attributes.index_image_url"
                                                            value={data.page.obj_attributes?.index_image_url || ''}
                                                            onChange={handleInputChange}
                                                            className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    {renderInput("Date Due", "page.date_due", "datetime-local", null, "Date fields for project management or event-based pages. Used for sorting and filtering.")}
                                                    {renderInput("Date Start", "page.date_start", "datetime-local", null, "Date fields for project management or event-based pages. Used for sorting and filtering.")}
                                                    {renderInput("Date End", "page.date_end", "datetime-local", null, "Date fields for project management or event-based pages. Used for sorting and filtering.")}
                                                    {renderInput("Date Completed", "page.date_completed", "datetime-local", null, "Date fields for project management or event-based pages. Used for sorting and filtering.")}
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="col-span-1">
                                                        <div className="flex items-center mb-1">
                                                            <label htmlFor="page.obj_attributes.sort_subpages_by" className="block text-sm font-medium text-gray-700">Sort Sub Pages By</label>
                                                            <HelpTooltip text="Determines the order of child pages when listed. Useful for blogs, news, or directories." />
                                                        </div>
                                                        <select
                                                            id="page.obj_attributes.sort_subpages_by"
                                                            name="page.obj_attributes.sort_subpages_by"
                                                            value={data.page.obj_attributes?.sort_subpages_by || ''}
                                                            onChange={handleInputChange}
                                                            className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                        >
                                                            <option value=""></option>
                                                            <option value="created_at">Created Date</option>
                                                            <option value="date_due">Date Due</option>
                                                            <option value="date_start">Start Date</option>
                                                            <option value="date_end">End Date</option>
                                                            <option value="date_completed">Date Completed</option>
                                                            <option value="updated_at">Updated Date</option>
                                                            <option value="title">Title</option>
                                                            <option value="rank">Rank</option>
                                                        </select>
                                                    </div>
                                                    <div className="col-span-1">
                                                        <div className="flex items-center mb-1">
                                                            <label htmlFor="page.obj_attributes.sort_direction" className="block text-sm font-medium text-gray-700">Sort Direction</label>
                                                            <HelpTooltip text="Ascending (A-Z, 0-9) or Descending (Z-A, 9-0) order for subpages." />
                                                        </div>
                                                        <select
                                                            id="page.obj_attributes.sort_direction"
                                                            name="page.obj_attributes.sort_direction"
                                                            value={data.page.obj_attributes?.sort_direction || ''}
                                                            onChange={handleInputChange}
                                                            className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                        >
                                                            <option value=""></option>
                                                            <option value="ASC">Ascending</option>
                                                            <option value="DESC">Descending</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        {renderCheckbox("Disable Smarty CSS", "page.obj_attributes.dont_use_smarty", null, "Disables Smarty processing for CSS/JS to prevent conflicts with curly braces.")}
                                                        {renderCheckbox("Disable JQuery", "page.obj_attributes.dont_use_jquery", null, "Prevents jQuery from loading on this specific page. Use to improve performance if not needed.")}
                                                        {renderCheckbox("Disable Menu CSS", "page.obj_attributes.dont_use_menu_css", null, "Excludes default menu styles. Useful for custom landing pages.")}
                                                        {renderCheckbox("Disable Content Builder CSS", "page.obj_attributes.dont_use_content_builder_css", null, "Removes styles injected by the content builder. Gives full control over styling.")}
                                                    </div>
                                                    <div className="space-y-2">
                                                        {renderCheckbox("Disable Bootstrap CSS", "page.obj_attributes.dont_use_bootstrap_css", null, "Removes Bootstrap framework styles. Essential for custom designs to avoid conflicts.")}
                                                        {renderCheckbox("Disable Livewire", "page.obj_attributes.dont_use_livewire", null, "Disables Laravel Livewire scripts. Use only if no dynamic components are present.")}
                                                        {renderCheckbox("Use Template as Only Base", "page.obj_attributes.use_template_as_only_base", null, "Ignores the parent template's layout and uses this page's content as the full document.")}
                                                        {renderCheckbox("Raw Content", "page.obj_attributes.raw_html", null, "Treats content as raw code without wrapping. Used for serving JSON, XML, or pure HTML fragments.")}
                                                        {data.page.obj_attributes?.raw_html == 1 && (
                                                            <div className="ml-6">
                                                                <div className="flex items-center mb-1">
                                                                    <label htmlFor="page.obj_attributes.content_type" className="block text-xs font-medium text-gray-700">Content Type</label>
                                                                    <HelpTooltip text="Sets the MIME type header (e.g., application/json). Required when serving non-HTML content." />
                                                                </div>
                                                                <select
                                                                    id="page.obj_attributes.content_type"
                                                                    name="page.obj_attributes.content_type"
                                                                    value={data.page.obj_attributes?.content_type || 'html'}
                                                                    onChange={handleInputChange}
                                                                    className="block w-full px-2 py-1 mt-1 text-xs bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                                                >
                                                                    <option value="html">HTML</option>
                                                                    <option value="json">JSON</option>
                                                                    <option value="javascript">JavaScript</option>
                                                                    <option value="css">CSS</option>
                                                                </select>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {data.page.category_id != 0 && (
                                    <div className="pt-4 space-y-4 border-t border-gray-200">
                                        <div>
                                            <div className="flex items-center mb-1">
                                                <label htmlFor="page.description" className="block text-sm font-medium text-gray-700">Description</label>
                                                <HelpTooltip text="Internal description for this template or component. Helps developers understand its purpose." />
                                            </div>
                                            <textarea
                                                id="page.description"
                                                name="page.description"
                                                rows={3}
                                                value={data.page.description || ''}
                                                onChange={handleInputChange}
                                                className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            />
                                        </div>
                                        {renderInput("HTML body elements", "page.obj_attributes.body_attributes", "text", null, "Attributes added to the <body> tag. Useful for page-specific CSS classes or JS hooks.")}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'html' && (
                    <div className="space-y-6">
                        {renderActionButtons()}
                        <div>
                            <label htmlFor="page.properties.cb_html" className="block text-sm font-medium text-gray-700">HTML Content</label>
                            <div className="mt-1 h-[600px] border border-gray-300 rounded-md overflow-hidden">
                                <Editor
                                    height="600px"
                                    defaultLanguage="html"
                                    value={data.page.properties?.cb_html || ''}
                                    onChange={(value, event) => handleEditorChange(value, event, 'page.properties.cb_html')}
                                    options={{
                                        minimap: { enabled: false },
                                        fontSize: 14,
                                        wordWrap: 'on',
                                        automaticLayout: true,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'styles' && (
                    <div className="space-y-6">
                        {renderActionButtons()}
                        <div>
                            <label htmlFor="page.properties.styles" className="block text-sm font-medium text-gray-700">CSS Styles</label>
                            <div className="mt-1 h-[600px] border border-gray-300 rounded-md overflow-hidden">
                                <Editor
                                    height="600px"
                                    defaultLanguage="css"
                                    value={data.page.properties?.styles || ''}
                                    onChange={(value, event) => handleEditorChange(value, event, 'page.properties.styles')}
                                    options={{
                                        minimap: { enabled: false },
                                        fontSize: 14,
                                        wordWrap: 'on',
                                        automaticLayout: true,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'related' && (
                    <div className="space-y-6">
                        {renderActionButtons()}
                        <div className="flex mb-4 space-x-2">
                            {!attachType && (
                                <>
                                    <button type="button" onClick={() => setAttachType('list')} className="px-3 py-1 text-sm text-white bg-indigo-600 rounded">Add List</button>
                                    <button type="button" onClick={() => setAttachType('report')} className="px-3 py-1 text-sm text-white bg-indigo-600 rounded">Add Report</button>
                                    <button type="button" onClick={() => setAttachType('record')} className="px-3 py-1 text-sm text-white bg-indigo-600 rounded">Add Record</button>
                                </>
                            )}
                            {attachType && (
                                <button type="button" onClick={() => { setAttachType(null); setSelectedAttachTid(''); setSelectedListForRecord(''); setSelectedRecordId(''); }} className="px-3 py-1 text-sm text-white bg-gray-500 rounded">Cancel</button>
                            )}
                        </div>

                        {attachType === 'list' && (
                            <div className="flex items-end space-x-2">
                                <div className="flex-grow">
                                    <label className="block text-sm font-medium text-gray-700">Select List</label>
                                    <select value={selectedAttachTid} onChange={(e) => setSelectedAttachTid(e.target.value)} className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                                        <option value=""></option>
                                        {data.lists.map(l => <option key={l.id} value={l.tid}>{l.title}</option>)}
                                    </select>
                                </div>
                                <button type="button" onClick={handleAttachObject} className="px-3 py-2 text-sm text-white bg-green-600 rounded">Attach</button>
                            </div>
                        )}

                        {attachType === 'report' && (
                            <div className="flex items-end space-x-2">
                                <div className="flex-grow">
                                    <label className="block text-sm font-medium text-gray-700">Select Report</label>
                                    <select value={selectedAttachTid} onChange={(e) => setSelectedAttachTid(e.target.value)} className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                                        <option value=""></option>
                                        {data.reports.map(r => <option key={r.id} value={r.tid}>{r.title}</option>)}
                                    </select>
                                </div>
                                <button type="button" onClick={handleAttachObject} className="px-3 py-2 text-sm text-white bg-green-600 rounded">Attach</button>
                            </div>
                        )}

                        {attachType === 'record' && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Select List</label>
                                    <select value={selectedListForRecord} onChange={(e) => setSelectedListForRecord(e.target.value)} className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                                        <option value=""></option>
                                        {data.lists.map(l => <option key={l.id} value={l.tid}>{l.title}</option>)}
                                    </select>
                                </div>
                                {selectedListForRecord && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Select Item</label>
                                        <select value={selectedRecordId} onChange={(e) => setSelectedRecordId(e.target.value)} className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                                            <option value=""></option>
                                            {listItems.map(i => <option key={i.id} value={i.id}>{i.title}</option>)}
                                        </select>
                                    </div>
                                )}
                                {selectedRecordId && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Item Name (for components)</label>
                                        <input type="text" value={recordName} onChange={(e) => setRecordName(e.target.value)} className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                                    </div>
                                )}
                                {selectedRecordId && (
                                    <button type="button" onClick={handleAttachItem} className="px-3 py-2 text-sm text-white bg-green-600 rounded">Attach Item</button>
                                )}
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                            <div>
                                <h4 className="mb-2 font-medium text-gray-900">Attached Lists & Reports</h4>
                                <ul className="bg-white border border-gray-200 divide-y divide-gray-200 rounded-md">
                                    {relatedObjects.attached_child_objs.length > 0 ? (
                                        relatedObjects.attached_child_objs.map((obj, idx) => (
                                            <li key={obj.id} className="flex items-center justify-between px-4 py-3">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{obj.title}</div>
                                                    <div className="text-xs text-gray-500">[{obj.tid}]</div>
                                                </div>
                                                <button type="button" onClick={() => handleDetachObject(obj.id)} className="text-sm text-red-600 hover:text-red-900">Detach</button>
                                            </li>
                                        ))
                                    ) : (
                                        <li className="px-4 py-3 text-sm text-gray-500">No related objects found.</li>
                                    )}
                                </ul>
                            </div>
                            <div>
                                <h4 className="mb-2 font-medium text-gray-900">Attached Items</h4>
                                <ul className="bg-white border border-gray-200 divide-y divide-gray-200 rounded-md">
                                    {relatedObjects.attached_items.length > 0 ? (
                                        relatedObjects.attached_items.map((item, idx) => (
                                            <li key={item.id} className="flex items-center justify-between px-4 py-3">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{item.title}</div>
                                                    <div className="text-xs text-gray-500">[{item.pivot?.meta_a || item.id}]</div>
                                                </div>
                                                <button type="button" onClick={() => handleDetachItem(item.id)} className="text-sm text-red-600 hover:text-red-900">Detach</button>
                                            </li>
                                        ))
                                    ) : (
                                        <li className="px-4 py-3 text-sm text-gray-500">No related items found.</li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'logs' && (
                    <div className="space-y-6">
                        {renderActionButtons()}
                        <WebpageLogs pageId={pageId} />
                    </div>
                )}

                {activeTab === 'backups' && (
                    <div className="space-y-6">
                        {renderActionButtons()}
                        <BackupManager
                            key={pageId}
                            entityLabel={data.page.category_id === 0 ? 'webpage' : 'template'}
                            fetchBackups={fetchBackups}
                            fetchBackupDetail={fetchBackupDetail}
                            restoreBackup={restoreBackupFromApi}
                            onRestoreSuccess={handleBackupRestored}
                        />
                    </div>
                )}

                {activeTab === 'import/export' && data.current_user_is_admin && (
                    <div className="space-y-6">
                        {renderActionButtons()}
                        <div className="p-4 text-red-800 border border-red-200 rounded-lg bg-red-50">
                            <p className="font-semibold">Danger Zone</p>
                            <p className="mt-1 text-sm">Importing invalid data can corrupt this webpage. Only proceed if you fully understand the JSON structure.</p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <button
                                type="button"
                                onClick={handleExportDataFetch}
                                disabled={exporting}
                                className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md disabled:opacity-50"
                            >
                                {exporting ? 'Exporting…' : 'Export Object'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setImportMode(true)}
                                disabled={importMode || !hasExportData}
                                className="px-4 py-2 text-sm text-white bg-yellow-500 rounded-md disabled:opacity-50"
                                title={hasExportData ? 'Confirm that you want to import the JSON currently loaded above.' : 'Paste or export JSON above before enabling import mode.'}
                            >
                                Enable Import Mode
                            </button>
                            <button
                                type="button"
                                onClick={() => copyToClipboard(exportData)}
                                disabled={!hasExportData}
                                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md disabled:opacity-50"
                            >
                                Copy JSON
                            </button>
                            <button
                                type="button"
                                onClick={handleDownloadExport}
                                disabled={!hasExportData}
                                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md disabled:opacity-50"
                            >
                                Download JSON
                            </button>
                            {importMode && (
                                <>
                                    <button
                                        type="button"
                                        onClick={handleImportConfirm}
                                        disabled={importing}
                                        className="px-4 py-2 text-sm text-white bg-red-600 rounded-md disabled:opacity-50"
                                    >
                                        {importing ? 'Importing…' : 'Confirm Import'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setImportMode(false)}
                                        disabled={importing}
                                        className="px-4 py-2 text-sm text-white bg-gray-500 rounded-md disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                </>
                            )}
                        </div>
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">Import / Export JSON</label>
                            <textarea
                                value={exportData}
                                onChange={(e) => setExportData(e.target.value)}
                                rows={14}
                                className="block w-full p-3 mt-1 font-mono text-sm border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="Click Export to load data or paste JSON here before importing."
                            />
                            <p className="mt-2 text-xs text-gray-500">Data includes all page attributes except identifiers. Review carefully before importing.</p>
                        </div>
                    </div>
                )}
            </form>
        </div>
    </div>
    );
};

export default WebpageSettings;
